import React, { useEffect, useState, useMemo } from "react";

import { useQuery } from "react-query";

import { debounce } from "lodash";

import {
  Card,
  CardContent,
  Grid,
  TextField,
  Divider,
  Typography,
} from "@material-ui/core";
import { Search } from "@material-ui/icons";

import { DestinationTable, DestinationTableSort } from "./DestinationTable";
import { DestinationUsageData, DestinationUsageByEnvData } from "../types";
import { makeRequest } from "../../util";

const useFetchDestinationUsage = () => {
  return useQuery("DestinationUsage", async () => {
    const { data: res } = await makeRequest<DestinationUsageData>(
      "get_destination_usage"
    );
    return res;
  });
};

const sortByProjFn = (sort: DestinationTableSort) => {
  return (a: DestinationUsageByEnvData, b: DestinationUsageByEnvData) => {
    if (!a.projName || !b.projName) {
      return 0;
    }
    if (sort.direction === "asc") {
      return a.projName.localeCompare(b.projName);
    }
    return b.projName.localeCompare(a.projName);
  };
};

const sortByEnvFn = (sort: DestinationTableSort) => {
  return (a: DestinationUsageByEnvData, b: DestinationUsageByEnvData) => {
    if (sort.direction === "asc") {
      return a.envName.localeCompare(b.envName);
    }
    return b.envName.localeCompare(a.envName);
  };
};

const sortByDestFn = (sort: DestinationTableSort) => {
  return (a: DestinationUsageByEnvData, b: DestinationUsageByEnvData) => {
    if (sort.direction === "asc") {
      return a.destination.localeCompare(b.destination);
    }
    return b.destination.localeCompare(a.destination);
  };
};

const DestinationUsage = () => {
  const { data, isLoading } = useFetchDestinationUsage();

  const destinationUsage: DestinationUsageByEnvData[] = useMemo(() => {
    return data?.destinationUsageByEnv ?? [];
  }, [data]);

  const eventDefCount: number = data?.eventDefCount ?? 0;

  const [searchString, setSearchString] = useState<string>("");
  const [searchStringDisplay, setSearchStringDisplay] = useState<string>("");

  const [sort, setSort] = useState<DestinationTableSort>({
    column: "projName",
    direction: "asc",
  });

  const onSearch = debounce((searchString: string) => {
    setSearchString(searchString.trim().toLowerCase());
  }, 500);

  const onSearchDisplay = (searchString: string) => {
    setSearchStringDisplay(searchString);
    onSearch(searchString);
  };

  useEffect(() => {
    return onSearch.cancel();
  }, [onSearch]);

  const filteredDestinations = useMemo(() => {
    if (!destinationUsage) {
      return [];
    }
    if (searchString === "") {
      return destinationUsage;
    }

    return destinationUsage.filter((d) => {
      const searchTarget = `${d.projName} ${d.envName} ${d.destination}`
        .trim()
        .toLowerCase();

      return searchTarget.includes(searchString);
    });
  }, [destinationUsage, searchString]);

  const sortedDestinations = useMemo(() => {
    if (sort.column === "projName") {
      return filteredDestinations.sort(sortByProjFn(sort));
    }

    if (sort.column === "envName") {
      return filteredDestinations.sort(sortByEnvFn(sort));
    }

    return filteredDestinations.sort(sortByDestFn(sort));
  }, [filteredDestinations, sort]);

  const distinctDestinations = (destinationUsage ?? []).reduce(
    (acc: string[], cur) => {
      if (!acc.includes(cur.destination)) {
        acc.push(cur.destination);
      }
      return acc;
    },
    []
  );

  return (
    <div className="h-full w-full">
      <Grid container spacing={5} className="mt-5">
        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <>
            <Grid item xs={6}>
              <Card className="px-20 py-16 border-none">
                <Typography variant="h1">
                  {distinctDestinations.length}
                </Typography>
                <Typography variant="h4">Distinct Destinations</Typography>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card className="px-20 py-16 border-none">
                <Typography variant="h1">{eventDefCount}</Typography>
                <Typography variant="h4">Event Definitions</Typography>
              </Card>
            </Grid>
          </>
        )}
      </Grid>
      <Grid container className="mt-5">
        <Grid item xs={9}>
          <Typography variant="h4">Destinations</Typography>
        </Grid>
        <Grid item xs={3} className="flex items-center">
          <Search />
          <TextField
            fullWidth
            placeholder="Search..."
            type="search"
            value={searchStringDisplay}
            variant="standard"
            onChange={(e) => onSearchDisplay(e.target.value)}
          />
        </Grid>
      </Grid>
      {isLoading ? null : (
        <Card className="min-w-[800px] min-h-[500px] relative">
          <>
            <Divider />
            <CardContent className="p-0 last:pb-0">
              <DestinationTable
                destinations={sortedDestinations ?? []}
                sort={sort}
                onSort={(newSortColumn) => {
                  const newSortDirection =
                    newSortColumn === sort.column
                      ? sort.direction === "asc"
                        ? "desc"
                        : "asc"
                      : "asc";

                  setSort({
                    column: newSortColumn,
                    direction: newSortDirection,
                  });
                }}
              />
            </CardContent>
          </>
        </Card>
      )}
    </div>
  );
};

export default DestinationUsage;
